import { get } from 'lodash';

import { APPLICANT_QUESTIONS, FIELD_PATHS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { PostProductSelectContract } from '@funnels/types';
import { SummaryStringDisplay } from '@funnels/ui-components';
import { Accordion, FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/Common';
import { capitalizeForEnumValue, getApplicantName } from '@funnels/utils';

interface ApplicantSummaryProps {
  applicantIndex: number;
  relatedPartyIndex: number;
  applicantSelect: PostProductSelectContract;
  handleEditClick?: () => void;
}

export const ApplicantSummary = ({
  applicantIndex,
  relatedPartyIndex,
  handleEditClick,
  applicantSelect,
}: ApplicantSummaryProps) => {
  const [formValues] = useFormValues();
  const applicants = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const questions = get({ postProductSelect: [applicantSelect] }, FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(0));
  const applicantId = get(formValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));
  const applicantName = getApplicantName(applicants, applicantId);
  const hasSpouseDetailsSection =
    questions.includes(APPLICANT_QUESTIONS.SPOUSE_FIRST_NAME) ||
    questions.includes(APPLICANT_QUESTIONS.SPOUSE_LAST_NAME);

  const spouseName = `${capitalizeForEnumValue(
    get(formValues, FIELD_PATHS.RELATED_PARTY_TITLE(relatedPartyIndex), '')
  )} ${get(formValues, FIELD_PATHS.RELATED_PARTY_FIRST_NAME(relatedPartyIndex), '')} ${get(
    formValues,
    FIELD_PATHS.RELATED_PARTY_LAST_NAME(relatedPartyIndex),
    ''
  )}`;

  return (
    <Accordion
      headerText="Applicant details"
      onPrimaryButtonClick={handleEditClick}
      primaryButtonLabel="Edit"
      headerSubText={applicantName}
    >
      <FunnelGrid>
        {questions.includes(APPLICANT_QUESTIONS.COUNTRY_OF_BIRTH) && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="Country of birth"
              value={get(formValues, FIELD_PATHS.APPLICANT_COUNTRY_OF_BIRTH(applicantIndex))}
            />
          </FunnelGridUnit>
        )}
        {questions.includes(APPLICANT_QUESTIONS.CITY_OF_BIRTH) && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="City of birth"
              value={get(formValues, FIELD_PATHS.APPLICANT_CITY_OF_BIRTH(applicantIndex))}
            />
          </FunnelGridUnit>
        )}
        {questions.includes(APPLICANT_QUESTIONS.COUNTRY_OF_CITIZENSHIP) && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="Country of citizenship"
              value={get(formValues, FIELD_PATHS.APPLICANT_CITIZENSHIP(applicantIndex))}
            />
          </FunnelGridUnit>
        )}
        {questions.includes(APPLICANT_QUESTIONS.MOTHERS_MAIDEN_NAME) && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="Mother's maiden name"
              value={get(formValues, FIELD_PATHS.APPLICANT_MOTHERS_MAIDEN_NAME(applicantIndex))}
            />
          </FunnelGridUnit>
        )}
        {hasSpouseDetailsSection && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay title="Spouse details" value={spouseName} />
          </FunnelGridUnit>
        )}
        {questions.includes(APPLICANT_QUESTIONS.POST_SETTLEMENT_MAILING_ADDRESS) && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="Post settlement mailing address"
              value={get(formValues, FIELD_PATHS.APPLICANT_POST_SETTLEMENT_ADDRESS_FORMATTED(applicantIndex))}
            />
          </FunnelGridUnit>
        )}
      </FunnelGrid>
    </Accordion>
  );
};
